// Libs
import { Box, Checkbox, FormControl, FormLabel, Typography } from "@material-ui/core";
import { useObserver } from "@shoothill/core";
// App
import { Stores, StoresContext } from "Globals/Stores";
import { CustomInput } from "Globals/Styles/AppStyling";
import { theme } from "Globals/Styles/AppTheme";
import { Row } from "Globals/Styles/LayoutStyles";
import ApplicantViewModel from "Globals/ViewModels/ApplicantViewModel";
// ViewModels
import ApplicationFormViewModel from "Globals/ViewModels/ApplicationFormViewModel";
import { CustomYesNoRadio } from "Globals/Views/CustomYesNoRadio";
import { CheckboxCheckedSVG } from "Globals/Views/IconSVGs/CheckboxCheckedSVG";
import { CheckboxSVG } from "Globals/Views/IconSVGs/CheckboxSVG";
import React, { useContext, useState } from "react";
import styled from "styled-components";

export const StyledTextAreaContainer = styled(Box)`
    width: 100%;
`;

interface IProps {
    applicant: ApplicantViewModel;
    applicationFormViewModel: ApplicationFormViewModel;
}

export const StatementDetailsView: React.FC<IProps> = (props: IProps) => {
    const storesContext = useContext<Stores>(StoresContext);
    const [viewModel] = useState(props.applicant.statementDetailsViewModel);
    const [showNotes, setShowNotes] = useState(false);

    return useObserver(() => (
        <div onMouseDown={() => viewModel.restartTimer()}>
            {viewModel.DevTool_ShowCanProceed && <h5 style={{ color: "red" }}>Can Proceed: {viewModel.canProceed ? "Yes" : "No"}</h5>}
            <Row style={{ alignItems: "center", flexDirection: "row" }}>
                <Checkbox
                    checked={!viewModel.model.hasActiveIVCA}
                    onChange={(e) => {
                        viewModel.handleCheckboxChange("hasActiveIVCA", !e.target.checked);
                    }}
                    icon={<CheckboxSVG />}
                    checkedIcon={<CheckboxCheckedSVG />}
                    disabled={props.applicationFormViewModel.model.isSubmitted}
                />
                <Typography variant="body1" style={{ marginLeft: "30px" }}>
                    I do not have any active individual voluntary credit agreements in place
                </Typography>
            </Row>
            <Row style={{ alignItems: "center", flexDirection: "row" }}>
                <Checkbox
                    checked={!viewModel.model.hasHadRepossession}
                    onChange={(e) => {
                        viewModel.handleCheckboxChange("hasHadRepossession", !e.target.checked);
                    }}
                    icon={<CheckboxSVG />}
                    checkedIcon={<CheckboxCheckedSVG />}
                    disabled={props.applicationFormViewModel.model.isSubmitted}
                />
                <Typography variant="body1" style={{ marginLeft: "30px" }}>
                    I have not had a home repossessed in the last 5 years
                </Typography>
            </Row>
            <Row style={{ alignItems: "center", flexDirection: "row" }}>
                <Checkbox
                    checked={viewModel.model.hasAccessToCosts}
                    onChange={(e) => {
                        viewModel.handleCheckboxChange("hasAccessToCosts", e.target.checked);
                    }}
                    icon={<CheckboxSVG />}
                    checkedIcon={<CheckboxCheckedSVG />}
                    disabled={props.applicationFormViewModel.model.isSubmitted}
                />
                <Box style={{ marginLeft: "30px" }}>
                    <Typography variant="body1">I/we have access to/can raise at least £3,000 to cover the costs of buying a home</Typography>
                    <Typography variant="body2" style={{ fontStyle: "italic" }}>
                        This amount is a guideline figure and will vary. In addition in this amount your mortgage lender will require a minimum 5-10% deposit of the share your
                        purchase.
                    </Typography>
                </Box>
            </Row>
            <Row style={{ alignItems: "center", flexDirection: "row" }}>
                <Checkbox
                    checked={!viewModel.model.hasLateLoanPayments}
                    onChange={(e) => {
                        viewModel.handleCheckboxChange("hasLateLoanPayments", !e.target.checked);
                    }}
                    icon={<CheckboxSVG />}
                    checkedIcon={<CheckboxCheckedSVG />}
                    disabled={props.applicationFormViewModel.model.isSubmitted}
                />
                <Typography variant="body1" style={{ marginLeft: "30px" }}>
                    I have never failed to keep up payments on any loan or form of credit agreement
                </Typography>
            </Row>
            <Row style={{ alignItems: "center", flexDirection: "row" }}>
                <Checkbox
                    checked={!viewModel.model.hasLateHomePayments}
                    onChange={(e) => {
                        viewModel.handleCheckboxChange("hasLateHomePayments", !e.target.checked);
                    }}
                    icon={<CheckboxSVG />}
                    checkedIcon={<CheckboxCheckedSVG />}
                    disabled={props.applicationFormViewModel.model.isSubmitted}
                />
                <Typography variant="body1" style={{ marginLeft: "30px" }}>
                    I have not been behind with rent/mortgage payments
                </Typography>
            </Row>
            <Row style={{ alignItems: "center", flexDirection: "row" }}>
                <Checkbox
                    checked={!viewModel.model.hasHadCCJ}
                    onChange={(e) => {
                        viewModel.handleCheckboxChange("hasHadCCJ", !e.target.checked);
                    }}
                    icon={<CheckboxSVG />}
                    checkedIcon={<CheckboxCheckedSVG />}
                    disabled={props.applicationFormViewModel.model.isSubmitted}
                />
                <Box style={{ marginLeft: "30px" }}>
                    <Typography variant="body1">I have never had a County Court Judgement registered against me</Typography>
                    <Typography variant="body2" style={{ fontStyle: "italic" }}>
                        If unticked, please provide date when discharged and summarise your circumstances below.
                    </Typography>
                </Box>
            </Row>
            <Row style={{ alignItems: "center", flexDirection: "row" }}>
                <Checkbox
                    checked={!viewModel.model.hasBeenBancrupt}
                    onChange={(e) => {
                        viewModel.handleCheckboxChange("hasBeenBancrupt", !e.target.checked);
                    }}
                    icon={<CheckboxSVG />}
                    checkedIcon={<CheckboxCheckedSVG />}
                    disabled={props.applicationFormViewModel.model.isSubmitted}
                />
                <Box style={{ marginLeft: "30px" }}>
                    <Typography variant="body1">I have never been declared bankrupt</Typography>
                    <Typography variant="body2" style={{ fontStyle: "italic" }}>
                        If unticked, please provide date when discharged and summarise your circumstances below.
                    </Typography>
                </Box>
            </Row>
            <Row style={{ alignItems: "center", flexDirection: "row" }}>
                <Checkbox
                    checked={!viewModel.model.isReceivingHousingBenefit}
                    onChange={(e) => {
                        viewModel.handleCheckboxChange("isReceivingHousingBenefit", !e.target.checked);
                    }}
                    icon={<CheckboxSVG />}
                    checkedIcon={<CheckboxCheckedSVG />}
                    disabled={props.applicationFormViewModel.model.isSubmitted}
                />
                <Typography variant="body1" style={{ marginLeft: "30px" }}>
                    I am not currently receiving housing benefits
                </Typography>
            </Row>
            <Box style={{ backgroundColor: "#F0F0F0", padding: "15px", marginTop: "30px" }}>
                <Row style={{ marginTop: "0px" }}>
                    <Typography variant="body2" style={{ fontWeight: "bold" }}>
                        If you cannot tick all the boxes, please summarise your circumstances by{" "}
                        <a style={{ color: "#007AFF", cursor: "pointer" }} onClick={() => (props.applicationFormViewModel.model.isSubmitted ? null : setShowNotes(!showNotes))}>
                            clicking here
                        </a>
                    </Typography>
                </Row>
                {((viewModel.model.notes != null && viewModel.model.notes != "") || showNotes) && (
                    <Row style={{ marginTop: "0px" }}>
                        <StyledTextAreaContainer>
                            <CustomInput
                                onChange={() => viewModel.handleInputChange()}
                                multiLine={true}
                                fullwidth={true}
                                placeHolder="Notes..."
                                viewModel={viewModel}
                                fieldName="notes"
                                inputProps={{
                                    disabled: props.applicationFormViewModel.model.isSubmitted,
                                }}
                            />
                        </StyledTextAreaContainer>
                    </Row>
                )}
            </Box>

            {showNotes === false && (
                <>
                    {viewModel.getError("notes") !== "" && <p style={{ color: theme.palette.error.main, position: "absolute", fontSize: "14px" }}>{viewModel.getError("notes")}</p>}
                </>
            )}

            <Row>
                <CustomYesNoRadio
                    label="Have you been employed by Hightown Housing Association within the last 12 months?"
                    fieldName={"hasBeenEmployedByHHA"}
                    value={viewModel.model.hasBeenEmployedByHHA}
                    onChange={viewModel.handleRadioChange}
                    disabled={props.applicationFormViewModel.model.isSubmitted}
                    error={viewModel.getError("hasBeenEmployedByHHA")}
                />
            </Row>
            <Row>
                <CustomYesNoRadio
                    label={
                        <span>
                            Are you closely connected to any employee or Board or Committee member of Hightown Housing Association? Do you, or anybody closely connected to you,
                            have any personal,
                            <br /> business or financial relationship with any Board, Committee or staff member of Hightown Housing Association?
                        </span>
                    }
                    fieldName={"hasRelationshipWithHHA"}
                    value={viewModel.model.hasRelationshipWithHHA}
                    onChange={viewModel.handleRadioChange}
                    disabled={props.applicationFormViewModel.model.isSubmitted}
                    error={viewModel.getError("hasRelationshipWithHHA")}
                />
            </Row>
            <Row>
                {viewModel.getValue<boolean>("hasRelationshipWithHHA") && (
                    <CustomInput
                        label="Please state"
                        placeHolder="Employee's Name"
                        viewModel={viewModel}
                        fieldName="hhaEmployeeName"
                        shrink={true}
                        onChange={() => viewModel.handleInputChange()}
                        inputProps={{
                            disabled: props.applicationFormViewModel.model.isSubmitted,
                        }}
                    />
                )}
            </Row>
            {viewModel.model.hasRelationshipWithHHA == true && (
                <>
                    <Row>
                        <Typography variant="body2" style={{ fontStyle: "italic" }}>
                            Closely connected includes family members and persons with whom the individual has a close connection. Family member has a wide meaning and would
                            include a partner (married, civil partnership or living together), parent, parent of a partner, son or daughter, stepson or stepdaughter, the child of a
                            partner, brother, sister, brother or sister of a partner, grandparent, grandchild, uncle or aunt, nephew or niece, the partners of any of these people,
                            any dependents, and any person on whom the individual depends. It also includes estranged, separated and divorced family members
                        </Typography>
                    </Row>
                    <Row>
                        <CustomInput
                            type="select"
                            style={{ minWidth: "350px" }}
                            placeHolder={"placeHolder"}
                            label={
                                <div>
                                    <p style={{ fontSize: "12px" }}>Please provide details of the relationship</p>
                                    <p style={{ fontSize: "10px" }}>
                                        (All interests declared will be reviewed by Hightown Housing Association who will consider whether there is any conflict or duality of
                                        interest.)
                                    </p>
                                </div>
                            }
                            viewModel={viewModel}
                            fieldName="relationshipTypeId"
                            selectItems={storesContext.Domain.RelationshipTypeStore.getRelationshipTypeOptions}
                        />
                    </Row>
                </>
            )}
        </div>
    ));
};
